import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Public as GoogleIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Clear as ClearIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import {
  getHotelsPropertyStatus,
  updateGoogleLiveStatus,
  updatePropertyLocationPoint,
  updatePropertyEmbedMap,
  updateAiPricingStatus,
} from '../api/adminPanelApi';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'pending', label: 'Pending' },
  { value: 'not_live', label: 'Not Live' },
  { value: 'no_gha', label: 'No GHA' },
];

const SORT_OPTIONS = [
  { value: 'hotel_id', label: 'Hotel ID' },
  { value: 'hotel_name', label: 'Hotel Name' },
  { value: 'stayflexi_hotel_id', label: 'StayFlexi ID' },
  { value: 'google_live_status', label: 'Google Status' },
  { value: 'ai_pricing', label: 'AI Pricing' },
];

const VALID_STATUSES = ['live', 'pending', 'not_live', 'no_gha'];
const VALID_AI_PRICING = ['active', 'inactive'];
const DEFAULT_ROWS_PER_PAGE = 50;

const normalizeStatus = (s) => (s || '').toLowerCase().replace(/\s+/g, '_');

const displayStatus = (s) => {
  const n = normalizeStatus(s);
  if (n === 'not_live') return 'Not Live';
  if (n === 'no_gha') return 'No GHA';
  if (n === 'live') return 'Live';
  if (n === 'pending') return 'Pending';
  return s || '–';
};

const getGoogleStatusTextColor = (status) => {
  const s = normalizeStatus(status);
  if (s === 'live') return '#2e7d32';
  if (s === 'not_live') return '#d32f2f';
  if (s === 'pending') return '#ed6c02';
  if (s === 'no_gha') return '#5e35b1';
  return 'inherit';
};

const getAiPricingTextColor = (status) => {
  const s = (status || '').toString().toLowerCase();
  if (s === 'active') return '#2e7d32';
  if (s === 'inactive') return '#d32f2f';
  return 'inherit';
};

const getRowId = (row) => row?.id ?? row?.hotel_id;

const hasStayflexiId = (row) => {
  const v = row?.stayflexi_hotel_id;
  if (v == null || v === '' || v === '–' || v === '-') return false;
  const n = Number(v);
  return !Number.isNaN(n) && n > 0;
};

const getLocationUrl = (value) => {
  const raw = (value || '').toString().trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const iframeMatch = raw.match(/src=["']([^"']+)["']/i);
  return iframeMatch ? iframeMatch[1] : null;
};

const truncateForDisplay = (str, maxLen = 45) => {
  if (!str || str.length <= maxLen) return str;
  return `${str.slice(0, maxLen)}…`;
};

const getRowEmbedMap = (row) => (row?.embed_map || '').toString().trim();

const getSortValue = (row, field) => {
  switch (field) {
    case 'hotel_id':
      return Number(row.hotel_id ?? row.id) || 0;
    case 'hotel_name':
      return (row.hotel_name || '').toLowerCase();
    case 'stayflexi_hotel_id':
      return Number(row.stayflexi_hotel_id) || 0;
    case 'google_live_status':
      return normalizeStatus(row.google_live_status);
    case 'ai_pricing':
      return (row.ai_pricing || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, field, order) => {
  const av = getSortValue(a, field);
  const bv = getSortValue(b, field);
  let cmp = 0;
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return order === 'desc' ? -cmp : cmp;
};

const rowMatchesSearch = (row, query) => {
  const q = query.toLowerCase();
  return (
    String(row.hotel_id ?? row.id ?? '').toLowerCase().includes(q) ||
    (row.hotel_name || '').toLowerCase().includes(q) ||
    String(row.stayflexi_hotel_id ?? '').toLowerCase().includes(q)
  );
};

const downloadExcel = (rows, filename) => {
  const headers = ['Hotel ID', 'Hotel Name', 'StayFlexi Hotel ID', 'Google Live Status', 'AI Pricing', 'Location Point', 'Embed Map'];
  const BOM = '\uFEFF';
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        `"${(r.hotel_id || r.id || '').toString().replace(/"/g, '""')}"`,
        `"${(r.hotel_name || '').toString().replace(/"/g, '""')}"`,
        `"${(r.stayflexi_hotel_id || '').toString().replace(/"/g, '""')}"`,
        `"${displayStatus(r.google_live_status).replace(/"/g, '""')}"`,
        `"${(r.ai_pricing || '').toString().replace(/"/g, '""')}"`,
        `"${(r.location_point || r.location || '').toString().replace(/"/g, '""')}"`,
        `"${getRowEmbedMap(r).replace(/"/g, '""')}"`,
      ].join(',')
    ),
  ];
  const csv = BOM + csvRows.join('\r\n');
  const blob = new Blob([csv], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `google_status_${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const GoogleStatus = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearch = useDeferredValue(searchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('hotel_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingAiId, setUpdatingAiId] = useState(null);
  const [locationEditRow, setLocationEditRow] = useState(null);
  const [locationEditValue, setLocationEditValue] = useState('');
  const [locationUpdating, setLocationUpdating] = useState(false);
  const [embedEditRow, setEmbedEditRow] = useState(null);
  const [embedEditValue, setEmbedEditValue] = useState('');
  const [embedUpdating, setEmbedUpdating] = useState(false);
  const [noStayflexiDialogOpen, setNoStayflexiDialogOpen] = useState(false);

  const isFiltering = searchTerm !== deferredSearch;

  const noStayflexiHotels = useMemo(
    () => list.filter((r) => !hasStayflexiId(r)),
    [list]
  );

  const fetchList = useCallback(async ({ notify = false } = {}) => {
    setLoading(true);
    try {
      const res = await getHotelsPropertyStatus();
      if (res.success && Array.isArray(res.data)) {
        setList(res.data);
        setSnackbar({
          open: true,
          message: `Loaded ${res.data.length} hotel(s)`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: res.error || 'Failed to load hotels',
          severity: 'error',
        });
        setList([]);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load', severity: 'error' });
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(0);
  }, [deferredSearch, statusFilter, sortField, sortOrder]);

  const filteredList = useMemo(() => {
    let rows = list;
    const status = statusFilter.toLowerCase();
    if (status !== 'all') {
      rows = rows.filter((r) => normalizeStatus(r.google_live_status) === status);
    }
    const q = deferredSearch.trim();
    if (q) {
      rows = rows.filter((r) => rowMatchesSearch(r, q));
    }
    return [...rows].sort((a, b) => compareRows(a, b, sortField, sortOrder));
  }, [list, statusFilter, deferredSearch, sortField, sortOrder]);

  const paginatedList = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, page, rowsPerPage]);

  const pageStart = filteredList.length === 0 ? 0 : page * rowsPerPage + 1;
  const pageEnd = Math.min((page + 1) * rowsPerPage, filteredList.length);

  const handleStatusChange = async (row, newStatus) => {
    const id = getRowId(row);
    if (!id || !VALID_STATUSES.includes(newStatus)) return;
    setUpdatingId(id);
    try {
      const res = await updateGoogleLiveStatus(id, newStatus);
      if (res.success) {
        setSnackbar({ open: true, message: `Status updated to "${displayStatus(newStatus)}"`, severity: 'success' });
        setList((prev) =>
          prev.map((r) => (getRowId(r) === id ? { ...r, google_live_status: newStatus } : r))
        );
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update', severity: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAiPricingChange = async (row, newStatus) => {
    const id = getRowId(row);
    if (!id || !VALID_AI_PRICING.includes(newStatus)) return;
    setUpdatingAiId(id);
    try {
      const res = await updateAiPricingStatus(id, newStatus);
      if (res.success) {
        setSnackbar({ open: true, message: `AI pricing updated to "${newStatus}"`, severity: 'success' });
        setList((prev) =>
          prev.map((r) => (getRowId(r) === id ? { ...r, ai_pricing: newStatus } : r))
        );
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update AI pricing', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update AI pricing', severity: 'error' });
    } finally {
      setUpdatingAiId(null);
    }
  };

  const handleDownloadExcel = () => {
    downloadExcel(filteredList, `google_status_${new Date().toISOString().slice(0, 10)}.xls`);
    setSnackbar({ open: true, message: 'Download started', severity: 'success' });
  };

  const openLocationEdit = (row) => {
    setLocationEditRow(row);
    setLocationEditValue(row.location_point || row.location || '');
  };

  const openEmbedEdit = (row) => {
    setEmbedEditRow(row);
    setEmbedEditValue(getRowEmbedMap(row));
  };

  const saveLocationEdit = async () => {
    if (!locationEditRow) return;
    const id = getRowId(locationEditRow);
    setLocationUpdating(true);
    try {
      const res = await updatePropertyLocationPoint(id, locationEditValue.trim());
      if (res.success) {
        setSnackbar({ open: true, message: 'Location updated', severity: 'success' });
        setList((prev) =>
          prev.map((r) => (getRowId(r) === id ? { ...r, location_point: locationEditValue.trim() } : r))
        );
        setLocationEditRow(null);
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update location', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update location', severity: 'error' });
    } finally {
      setLocationUpdating(false);
    }
  };

  const saveEmbedEdit = async () => {
    if (!embedEditRow) return;
    const hotelId = getRowId(embedEditRow);
    const embedMap = embedEditValue.trim();
    if (!embedMap) {
      setSnackbar({ open: true, message: 'Please enter an embed map value', severity: 'warning' });
      return;
    }
    setEmbedUpdating(true);
    try {
      const res = await updatePropertyEmbedMap(hotelId, embedMap);
      if (res.success) {
        setSnackbar({ open: true, message: res.message || 'Embed map updated', severity: 'success' });
        setList((prev) =>
          prev.map((r) => (getRowId(r) === hotelId ? { ...r, embed_map: embedMap } : r))
        );
        setEmbedEditRow(null);
        setEmbedEditValue('');
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update embed map', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update embed map', severity: 'error' });
    } finally {
      setEmbedUpdating(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700', '&:hover': { backgroundColor: 'rgba(255,119,0,0.1)' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GoogleIcon sx={{ color: '#4285f4', fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Google Live Status
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadExcel}
            disabled={filteredList.length === 0}
            sx={{ borderColor: '#4caf50', color: '#4caf50' }}
          >
            Download Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkOffIcon />}
            onClick={() => setNoStayflexiDialogOpen(true)}
            disabled={loading || list.length === 0}
            sx={{
              borderColor: noStayflexiHotels.length > 0 ? '#d32f2f' : '#9e9e9e',
              color: noStayflexiHotels.length > 0 ? '#d32f2f' : '#9e9e9e',
              fontWeight: 600,
            }}
          >
            No StayFlexi ID ({noStayflexiHotels.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchList({ notify: true })}
            disabled={loading}
            sx={{ borderColor: '#FF7700', color: '#FF7700' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Hotel ID, name, or StayFlexi ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end" aria-label="Clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ opacity: isFiltering ? 0.85 : 1, transition: 'opacity 0.15s' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Google status</InputLabel>
              <Select value={statusFilter} label="Google status" onChange={(e) => setStatusFilter(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Sort by</InputLabel>
              <Select value={sortField} label="Sort by" onChange={(e) => setSortField(e.target.value)}>
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value)}>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {filteredList.length === 0
                ? `0 of ${list.length} hotels`
                : `Showing ${pageStart}–${pageEnd} of ${filteredList.length}${filteredList.length !== list.length ? ` (${list.length} total)` : ''}`}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'auto', opacity: isFiltering ? 0.7 : 1, transition: 'opacity 0.15s' }}>
        <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '9%' }}>Hotel ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel Name</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '10%' }}>StayFlexi Hotel ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '14%' }}>Google Live Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: '11%' }}>AI Pricing</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Location Point</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: 112, maxWidth: 112, px: 1, whiteSpace: 'nowrap' }}>Embed Map</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {list.length === 0 ? 'No hotels loaded.' : 'No hotels match your search or filters.'}
                </TableCell>
              </TableRow>
            )}
            {paginatedList.map((row, idx) => {
              const rowId = getRowId(row);
              const status = normalizeStatus(row.google_live_status);
              const aiPricingStatus = (row.ai_pricing || '').toString().toLowerCase();
              const isUpdating = updatingId === rowId;
              const isAiUpdating = updatingAiId === rowId;
              const location = row.location_point || row.location || '';
              const locationUrl = getLocationUrl(location);
              const locationDisplay = truncateForDisplay(location || '–', 42);
              const hasEmbed = !!getRowEmbedMap(row);

              return (
                <TableRow key={`${row.hotel_id ?? row.id}-${idx}`} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.hotel_id ?? row.id ?? '–'}</TableCell>
                  <TableCell>{row.hotel_name || '–'}</TableCell>
                  <TableCell>{row.stayflexi_hotel_id ?? '–'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        size="small"
                        value={VALID_STATUSES.includes(status) ? status : 'not_live'}
                        disabled={isUpdating}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        sx={{
                          minWidth: 130,
                          '& .MuiSelect-select': {
                            color: getGoogleStatusTextColor(status),
                            fontWeight: 700,
                          },
                        }}
                      >
                        <MenuItem value="live" sx={{ color: '#2e7d32', fontWeight: 700 }}>Live</MenuItem>
                        <MenuItem value="pending" sx={{ color: '#ed6c02', fontWeight: 700 }}>Pending</MenuItem>
                        <MenuItem value="not_live" sx={{ color: '#d32f2f', fontWeight: 700 }}>Not Live</MenuItem>
                        <MenuItem value="no_gha" sx={{ color: '#5e35b1', fontWeight: 700 }}>No GHA</MenuItem>
                      </Select>
                      {isUpdating && <CircularProgress size={16} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        size="small"
                        value={VALID_AI_PRICING.includes(aiPricingStatus) ? aiPricingStatus : 'active'}
                        disabled={isAiUpdating}
                        onChange={(e) => handleAiPricingChange(row, e.target.value)}
                        sx={{
                          minWidth: 120,
                          '& .MuiSelect-select': {
                            color: getAiPricingTextColor(aiPricingStatus),
                            fontWeight: 700,
                          },
                        }}
                      >
                        <MenuItem value="active" sx={{ color: '#2e7d32', fontWeight: 700 }}>Active</MenuItem>
                        <MenuItem value="inactive" sx={{ color: '#d32f2f', fontWeight: 700 }}>Inactive</MenuItem>
                      </Select>
                      {isAiUpdating && <CircularProgress size={16} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {locationUrl ? (
                        <Typography
                          component="a"
                          href={locationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          title={location}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {locationDisplay}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          title={location}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {locationDisplay}
                        </Typography>
                      )}
                      <IconButton size="small" onClick={() => openLocationEdit(row)} title="Edit location point">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 112, maxWidth: 112, px: 1, whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      variant={hasEmbed ? 'outlined' : 'contained'}
                      startIcon={<MapIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openEmbedEdit(row)}
                      title={hasEmbed ? 'Update embed map' : 'Add embed map'}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        py: 0.35,
                        px: 0.75,
                        ...(hasEmbed
                          ? { borderColor: '#2e7d32', color: '#2e7d32', '&:hover': { borderColor: '#1b5e20', bgcolor: 'rgba(46,125,50,0.04)' } }
                          : { bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }),
                      }}
                    >
                      {hasEmbed ? 'Update' : 'Add map'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredList.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      <Dialog
        open={noStayflexiDialogOpen}
        onClose={() => setNoStayflexiDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOffIcon sx={{ color: '#d32f2f' }} />
          Hotels without StayFlexi ID ({noStayflexiHotels.length})
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {noStayflexiHotels.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              All loaded hotels have a StayFlexi ID.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Hotel ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hotel Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Google Live Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>AI Pricing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {noStayflexiHotels.map((row, idx) => (
                    <TableRow key={`no-sf-${getRowId(row)}-${idx}`} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.hotel_id ?? row.id ?? '–'}</TableCell>
                      <TableCell>{row.hotel_name || '–'}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: getGoogleStatusTextColor(row.google_live_status) }}
                        >
                          {displayStatus(row.google_live_status)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: getAiPricingTextColor(row.ai_pricing) }}
                        >
                          {(row.ai_pricing || '–').toString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoStayflexiDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!locationEditRow} onClose={() => !locationUpdating && setLocationEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit location point</DialogTitle>
        <DialogContent>
          {locationEditRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {locationEditRow.hotel_name} · Hotel ID {getRowId(locationEditRow)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Location point"
            value={locationEditValue}
            onChange={(e) => setLocationEditValue(e.target.value)}
            placeholder="e.g. 28.6139,77.2090"
            sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationEditRow(null)} disabled={locationUpdating}>Cancel</Button>
          <Button variant="contained" onClick={saveLocationEdit} disabled={locationUpdating}>
            {locationUpdating ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!embedEditRow} onClose={() => !embedUpdating && setEmbedEditRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>{getRowEmbedMap(embedEditRow) ? 'Update embed map' : 'Add embed map'}</DialogTitle>
        <DialogContent>
          {embedEditRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {embedEditRow.hotel_name} · Hotel ID {getRowId(embedEditRow)}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={12}
            label="Google Maps embed"
            value={embedEditValue}
            onChange={(e) => setEmbedEditValue(e.target.value)}
            placeholder="Paste embed iframe HTML or URL — sent to API exactly as entered"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedEditRow(null)} disabled={embedUpdating}>Cancel</Button>
          <Button variant="contained" onClick={saveEmbedEdit} disabled={embedUpdating || !embedEditValue.trim()}>
            {embedUpdating ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoogleStatus;
